import { useEffect, useState } from "react";
import { Loader2, PlusCircle, Save, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { MonthPicker } from "@/components/MonthPicker";
import { EditorBlock, type Block } from "@/components/budget/BudgetLines";
import { PlanSummary } from "@/components/budget/PlanSummary";
import { TrackingSection } from "@/components/budget/BudgetTracking";
import { ScopeDialog } from "@/components/budget/ScopeDialog";
import { UnsavedDialog } from "@/components/budget/UnsavedDialog";
import { AlertsCard, MentorTeaser, ToAssignCard } from "@/components/budget/SideCards";
import { api, formatApiError } from "@/lib/api";
import type { BudgetResponse } from "@/lib/budget";
import { currentMonth, monthName } from "@/lib/format";
import { buildTracking } from "@/lib/tracking";
import { planTotals, useBudgetDraft, type PlanScope } from "@/lib/useBudgetLines";

const ORDER: Block[] = ["income", "savings", "expense", "debt"];

export default function BudgetPage() {
  const [month, setMonth] = useState(currentMonth());
  const ctl = useBudgetDraft(month);
  const totals = planTotals(ctl.draft ?? [], ctl.categories, month);
  const hasLines = (ctl.draft ?? []).length > 0;
  const [askScope, setAskScope] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const changeMonth = (m: string) => (ctl.editing && ctl.dirty ? setPending(m) : setMonth(m));
  const openScope = () => { if (ctl.validate()) setAskScope(true); };
  const pick = async (s: PlanScope) => { if (await ctl.save(s)) setAskScope(false); };
  const [budget, setBudget] = useState<BudgetResponse | null>(null);
  useEffect(() => {
    if (ctl.editing) return;
    setBudget(null);
    api.get<BudgetResponse>("/budget", { params: { month } }).then((r) => setBudget(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [month, ctl.editing]);

  const actions = ctl.editing ? (
    <div className="flex gap-2">
      <Button variant="outline" onClick={ctl.cancel} disabled={ctl.saving} className="h-11 border-[#DDE1E8]" data-testid="budget-cancel-button">Cancelar</Button>
      <Button onClick={openScope} disabled={ctl.saving || !hasLines} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="budget-save-button">
        {ctl.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Guardar</>}
      </Button>
    </div>
  ) : (
    <Button onClick={ctl.startEdit} disabled={!ctl.draft} variant="outline" className="h-11 border-[#1B2A4A] text-[#1B2A4A]" data-testid="budget-settings-button">
      <Settings2 className="mr-2 h-4 w-4" /> Ajustes
    </Button>
  );

  return (
    <section data-testid="budget-page">
      <PageHeader title="Presupuesto" testId="budget-title" actions={actions} />
      <p className="-mt-2 mb-6 text-sm text-slate-500" data-testid="budget-mode-text">
        {ctl.editing ? `Editando el presupuesto de ${monthName(month)}. Primero tu ahorro, después tus gastos.` : "Cómo vas este mes contra tu presupuesto. Da clic en una categoría para ver sus conceptos."}
      </p>
      {!ctl.editing && (
        <div className="fade-up grid gap-6 lg:grid-cols-[1.7fr_1fr]">
          <div className="min-w-0 space-y-6">
            <MonthPicker month={month} onChange={changeMonth} />
            {ctl.draft && !hasLines && (
              <div className="flex flex-col gap-3 rounded-[24px] bg-[#DFE7F3] p-6 sm:flex-row sm:items-center sm:justify-between" data-testid="budget-empty-banner">
                <div>
                  <p className="font-semibold text-[#16294D]">Aún no tienes presupuesto para {monthName(month)}</p>
                  <p className="text-sm text-[#1A1F2B]">Mientras tanto, aquí ves cómo se comportan tus movimientos del mes.</p>
                </div>
                <Button onClick={ctl.startEdit} className="h-11 shrink-0 bg-[#16294D] text-white hover:bg-[#22396A]" data-testid="budget-create-button">
                  <PlusCircle className="mr-2 h-4 w-4" /> Crear mi presupuesto
                </Button>
              </div>
            )}
            {!budget ? <div className="h-72 animate-pulse rounded-[14px] bg-[#F4F5F7]" /> : buildTracking(budget).map((s) => <TrackingSection key={s.key} s={s} />)}
          </div>
          <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
            <ToAssignCard t={totals} />
            {budget && <AlertsCard data={budget} />}
            <MentorTeaser />
          </div>
        </div>
      )}
      {ctl.editing && (
      <div className="fade-up grid gap-6 lg:grid-cols-[1.7fr_1fr]">
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3" data-testid="editing-month-bar">
            <MonthPicker month={month} onChange={changeMonth} />
            {ctl.dirty && <span className="text-sm font-medium text-amber-700" data-testid="unsaved-indicator">Cambios sin guardar</span>}
          </div>
          {ORDER.map((s) => <EditorBlock key={s} section={s} ctl={ctl} />)}
          {ctl.editing && hasLines && (
            <Button onClick={openScope} disabled={ctl.saving} className="h-12 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="budget-save-bottom">
              {ctl.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar presupuesto"}
            </Button>
          )}
        </div>
        <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
          <ToAssignCard t={totals} />
          <PlanSummary t={totals} />
          <MentorTeaser />
        </div>
      </div>
      )}
      <ScopeDialog open={askScope} month={month} saving={ctl.saving} onOpenChange={setAskScope} onPick={pick} />
      <UnsavedDialog month={month} open={!!pending} onStay={() => setPending(null)} onLeave={() => { if (pending) setMonth(pending); setPending(null); }} />
    </section>
  );
}
